import React, { useEffect } from "react";
import { useFormik } from "formik";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import DashboardLayout from "../../components/SharedLayouts/DashboardLayout";
import BackArrow2 from "../../components/BackArrow2";
import Loader from "../../components/SharedLayouts/Loader";
import IpoForm, { stockSourceOptions } from "./IpoForm";
import { buildIpoPayload } from "./buildIpoPayload";
import { getIpo } from "../../store/actions/ipos/getIpo";
import { getIpoSubscriptions } from "../../store/actions/ipos/getIpoSubscriptions";
import { updateIpo } from "../../store/actions/ipos/updateIpo";
import { createIpoSchema } from "../../Helper/validationSchemas";
import { ipoStatusOptions } from "../../config/data";
import { currencyStockOptions } from "../Stocks/StockForm";
import { getIpoSubscriptionCount } from "./ipoDetailHelpers";

function EditIpo() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const ipo = useSelector((state) => state?.ipos?.ipo);
  const ipoDetail = useSelector((state) => state?.ipos?.ipoDetail);
  const subscriptionsSummary = useSelector(
    (state) => state?.ipos?.ipoSubscriptionsSummary
  );
  const fetchPending = useSelector((state) => state?.ipos?.getIpoPending) || false;
  const loading = useSelector((state) => state?.ipos?.updatePending) || false;

  useEffect(() => {
    dispatch(getIpo(id));
    dispatch(getIpoSubscriptions({ id }));
  }, [dispatch, id]);

  const offerPriceLocked =
    getIpoSubscriptionCount(ipoDetail, subscriptionsSummary) > 0;

  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      // PUT echoes the stock fields back (Postman "Edit an IPO" sends the
      // full create body); the API ignores them on edit.
      stock_source: stockSourceOptions[0],
      shariah_stock_id: ipo?.shariah_stock_id ?? "",
      symbol: ipo?.stock?.symbol || "",
      name: ipo?.stock?.name || "",
      currency:
        currencyStockOptions.find((opt) => opt.value === ipo?.currency) ||
        currencyStockOptions[0],
      exchange: ipo?.stock?.exchange || "",
      sector: ipo?.stock?.sector || "",
      logo_url: ipo?.stock?.logo_url || "",
      offer_price: ipo?.offer_price ?? "",
      offer_open_date: ipo?.offer_open_date ? dayjs(ipo.offer_open_date) : null,
      offer_close_date: ipo?.offer_close_date
        ? dayjs(ipo.offer_close_date)
        : null,
      min_subscription_shares: ipo?.min_subscription_shares ?? "",
      admin_fee_pct: ipo?.admin_fee_pct ?? "",
      total_shares_on_offer: ipo?.total_shares_on_offer ?? "",
      issuer: ipo?.issuer || "",
      overview: ipo?.overview || "",
      highlights: ipo?.highlights || "",
      key_information: ipo?.key_information || "",
      risk_disclosure: ipo?.risk_disclosure || "",
      prospectus_url: ipo?.prospectus_url || "",
      status:
        ipoStatusOptions.find((opt) => opt.value === ipo?.status) ||
        ipoStatusOptions[0],
    },
    validationSchema: createIpoSchema,
    onSubmit: (values, { setSubmitting }) => {
      const data = buildIpoPayload(values);
      // Locked once subscribed (ipo.md §8); leave it out rather than risk a 422.
      if (offerPriceLocked) {
        delete data.offer_price;
      }
      dispatch(updateIpo({ id, data, navigate, setSubmitting }));
    },
  });

  return (
    <DashboardLayout>
      <div className="px-2 lg:px-8">
        <div className="flex items-center mb-8 flex-row md:items-center">
          <Link to={"/dashboard"}>
            <img src="/Assets/bread.png" alt="" className="w-8 h-8 mr-2" />
          </Link>
          <img src="/Assets/chev.png" alt="" className="w-8 h-8 mr-2" />
          <Link to="/ipos" className="font-semibold text-[#475467] text-base flex">
            IPOs
          </Link>
          <img src="/Assets/chev.png" alt="" className="w-8 h-8 md:mr-2" />
          <span className="font-semibold text-[#475467] text-base bg-[#F9FAFB] p-1 rounded-md">
            Edit
          </span>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center mt-8 mb-4 md:mb-6">
          <h1 className="font-semibold text-xl md:text-2xl w-full">
            Edit IPO {ipo?.stock?.symbol ? `— ${ipo.stock.symbol}` : ""}
          </h1>
        </div>
        <BackArrow2 mb={20} ml={0} />

        {fetchPending && !ipo ? (
          <Loader />
        ) : (
          <IpoForm
            form={form}
            loading={loading}
            submitLabel="Update IPO"
            isEdit
            offerPriceLocked={offerPriceLocked}
            onCancel={() => navigate(-1)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

export default EditIpo;
